/**
 * 知识库文档表单模态框
 *
 * 新建模式：文件上传（Upload.Dragger）+ 切片配置
 * 编辑模式：修改标题/分类/标签
 */

import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Upload, message, Typography } from 'antd';
import { InboxOutlined, FileOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { knowledgeApi, type ChunkConfig } from '../../services/api';
import { ChunkConfigInput } from '../knowledge/ChunkConfigInput';

const { Text } = Typography;

interface KnowledgeFormModalProps {
    open: boolean;
    editData?: any;
    onClose: () => void;
    onSuccess: () => void;
}

const categoryOptions = [
    { label: '售后服务', value: '售后' },
    { label: '产品咨询', value: '产品' },
    { label: '订单管理', value: '订单' },
    { label: '技术支持', value: '技术' },
    { label: '其他', value: '其他' },
];

const ALLOWED_EXTENSIONS = ['.md', '.txt', '.pdf', '.docx', '.xlsx'];

const KnowledgeFormModal: React.FC<KnowledgeFormModalProps> = ({
    open,
    editData,
    onClose,
    onSuccess,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [chunkConfig, setChunkConfig] = useState<ChunkConfig | undefined>();
    const [fileSize, setFileSize] = useState<number | undefined>();
    const isEdit = !!editData;

    useEffect(() => {
        if (open && editData) {
            form.setFieldsValue({
                title: editData.title,
                category: editData.category,
                tags: editData.tags?.join(', ') || '',
            });
            setFileList([]);
            setFileSize(undefined);
            setChunkConfig(undefined);
        } else if (open) {
            form.resetFields();
            setFileList([]);
            setFileSize(undefined);
            setChunkConfig(undefined);
        }
    }, [open, editData, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const tags = values.tags
                ? values.tags.split(/[,，]/).map((t: string) => t.trim()).filter(Boolean)
                : [];

            if (isEdit) {
                // 编辑模式：JSON 更新
                await knowledgeApi.update(editData.id, {
                    title: values.title,
                    category: values.category,
                    tags,
                });
                message.success('文档更新成功');
            } else {
                // 新建模式：文件上传（带切片配置）
                if (fileList.length === 0) {
                    message.error('请上传文件');
                    setLoading(false);
                    return;
                }

                const file = fileList[0].originFileObj as File;

                // 使用新的 uploadWithConfig API
                await knowledgeApi.uploadWithConfig({
                    title: values.title,
                    category: values.category,
                    tags: JSON.stringify(tags),
                    file: file,
                    chunk_size: chunkConfig?.chunk_size,
                    chunk_overlap: chunkConfig?.chunk_overlap || 50,
                });
                message.success('文档上传成功，正在索引中...');
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            if (err.response?.data?.detail) {
                message.error(err.response.data.detail);
            } else if (err.response?.data?.error?.message) {
                message.error(err.response.data.error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={isEdit ? '编辑文档' : '上传文档'}
            open={open}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={loading}
            okText={isEdit ? '保存' : '上传'}
            cancelText="取消"
            width={640}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                style={{ marginTop: 16 }}
                requiredMark={false}
            >
                <Form.Item
                    name="title"
                    label="文档标题"
                    rules={[{ required: true, message: '请输入文档标题' }]}
                >
                    <Input placeholder="请输入文档标题" />
                </Form.Item>

                {!isEdit && (
                    <>
                        <Form.Item
                            label="上传文件"
                            required
                            extra={
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    支持格式：{ALLOWED_EXTENSIONS.join(', ')}，最大 100MB
                                </Text>
                            }
                        >
                            <Upload.Dragger
                                fileList={fileList}
                                maxCount={1}
                                beforeUpload={(file) => {
                                    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
                                    if (!ALLOWED_EXTENSIONS.includes(ext)) {
                                        message.error(`不支持的文件格式：${ext}`);
                                        return Upload.LIST_IGNORE;
                                    }
                                    if (file.size > 100 * 1024 * 1024) {
                                        message.error('文件不能超过 100MB');
                                        return Upload.LIST_IGNORE;
                                    }
                                    const uploadFile: UploadFile = {
                                        uid: file.uid || `upload-${Date.now()}`,
                                        name: file.name,
                                        size: file.size,
                                        type: file.type,
                                        originFileObj: file as any,
                                    };
                                    setFileList([uploadFile]);
                                    setFileSize(file.size);
                                    // 自动填充标题
                                    const currentTitle = form.getFieldValue('title');
                                    if (!currentTitle) {
                                        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                                        form.setFieldValue('title', nameWithoutExt);
                                    }
                                    return false; // 阻止自动上传
                                }}
                                onRemove={() => {
                                    setFileList([]);
                                    setFileSize(undefined);
                                }}
                            >
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined style={{ fontSize: 40, color: '#3b82f6' }} />
                                </p>
                                <p className="ant-upload-text" style={{ fontWeight: 500 }}>
                                    点击或拖拽文件到此区域上传
                                </p>
                                <p className="ant-upload-hint" style={{ color: '#71717a' }}>
                                    支持 Markdown、TXT、PDF、Word、Excel 格式
                                </p>
                            </Upload.Dragger>
                        </Form.Item>

                        {/* 切片配置 */}
                        {fileList.length > 0 && (
                            <Form.Item label=" ">
                                <ChunkConfigInput
                                    fileSize={fileSize}
                                    fileName={fileList[0]?.name}
                                    value={chunkConfig}
                                    onChange={setChunkConfig}
                                />
                            </Form.Item>
                        )}
                    </>
                )}

                {isEdit && editData?.file_info && (
                    <Form.Item label="已上传文件">
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 12px',
                            background: '#f4f4f5',
                            borderRadius: 8,
                        }}>
                            <FileOutlined style={{ color: '#3b82f6' }} />
                            <Text>{editData.file_info.name}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                ({(editData.file_info.size / 1024).toFixed(1)} KB)
                            </Text>
                        </div>
                    </Form.Item>
                )}

                <Form.Item name="category" label="分类">
                    <Select
                        placeholder="选择分类"
                        options={categoryOptions}
                        allowClear
                    />
                </Form.Item>

                <Form.Item name="tags" label="标签">
                    <Input placeholder="多个标签用逗号分隔，如：退货, 退款" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default KnowledgeFormModal;
